import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  ElementRef,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  OnDestroy,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { NumberOrString as NumberOrStringPipe } from './pipes/number-or-string.pipe';
import { CdkDrag, CdkDragDrop, CdkDragPreview, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { DataTable as DataTableService } from './data-table.svc';
import { DataTableColumn, DataTableState } from './DataTable';

@Component({
  selector: 'section[oke-data-table]',
  imports: [NumberOrStringPipe, CdkDropList, CdkDrag, CdkDragPreview],
  providers: [DataTableService],
  templateUrl: './data-table.html',
  styleUrl: './data-table.scss',
  host: {
    '[class.is-collapsed]': 'this.$state().isCollapsed'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTable<T> implements OnDestroy, AfterViewInit {
  // --- Service(s) ---
  private readonly _dataTableSvc: DataTableService = inject(DataTableService);
  // --- Signals (Inputs & Models) ---
  protected readonly $data: InputSignal<T[]> = input.required<T[]>();
  protected readonly $columns: ModelSignal<DataTableColumn<T>[]> = model.required<DataTableColumn<T>[]>();
  protected readonly $footer: InputSignal<{ [K in keyof T]?: any } | null> = input<{ [K in keyof T]?: any } | null>(
    null
  );
  protected readonly $caption: InputSignal<string | null> = input<string | null>(null);
  private readonly _$captionProjection: Signal<ElementRef<HTMLTableCaptionElement> | undefined> = contentChild<
    ElementRef<HTMLTableCaptionElement> | undefined
  >('okeDataTableCaption');
  protected readonly isRendered: OutputEmitterRef<this> = output<this>();
  // --- Internal State Signals ---
  private readonly _$sortColumn: WritableSignal<keyof T | null> = signal<keyof T | null>(null);
  protected readonly $sortColumn: Signal<keyof T | null> = this._$sortColumn.asReadonly();
  private readonly _$sortDirection: WritableSignal<'asc' | 'desc' | null> = signal<'asc' | 'desc' | null>(null);
  protected readonly $sortDirection: Signal<'asc' | 'desc' | null> = this._$sortDirection.asReadonly();
  private readonly _$expandedRows: WritableSignal<Set<number>> = signal<Set<number>>(new Set<number>());
  protected readonly $expandedRows: Signal<Set<number>> = this._$expandedRows.asReadonly();
  private readonly _$hiddenColumnKeys: WritableSignal<Set<keyof T>> = signal<Set<keyof T>>(new Set<keyof T>());
  protected readonly $hiddenColumnKeys: Signal<Set<keyof T>> = this._$hiddenColumnKeys.asReadonly();
  // --- Computed Signals ---
  protected readonly $visibleColumns: Signal<DataTableColumn<T>[]> = computed(() => {
    const allColumns = this.$columns();
    const hiddenColumnKeys = this.$hiddenColumnKeys();
    const data = this.$data();
    return this._dataTableSvc.getSanitizedVisibleColumns<T>(allColumns, hiddenColumnKeys, data);
  });
  protected readonly $hiddenColumns: Signal<DataTableColumn<T>[]> = computed(() =>
    this.$columns().filter(col => this.$hiddenColumnKeys().has(col.key))
  );
  protected readonly $processedData: Signal<T[]> = computed(() => {
    const data = [...this.$data()];
    const COL = this.$sortColumn();
    const DIR = this.$sortDirection();

    if (!COL || !DIR) {
      return data;
    }

    return data.sort((a, b) => {
      const valueA = this._dataTableSvc.normalizeForSort(a[COL] as string | number | null | undefined);
      const valueB = this._dataTableSvc.normalizeForSort(b[COL] as string | number | null | undefined);
      if (valueA < valueB) {
        return DIR === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return DIR === 'asc' ? 1 : -1;
      }
      return 0;
    });
  });
  readonly $state: Signal<DataTableState> = computed(() => this._dataTableSvc.$state());
  protected readonly $browserHasPointer: Signal<boolean> = computed(() => this._dataTableSvc.$browserHasPointer());
  protected readonly $isCaptionExists: Signal<boolean> = computed(() => {
    const captionInput = this.$caption();
    const captionProjection = this._$captionProjection();
    const isInput = captionInput !== null && captionInput !== undefined;
    const isProjection = captionProjection !== null && captionProjection !== undefined;
    return isInput || isProjection;
  });
  // --- Private Properties ---
  private readonly _hostElem: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);
  private readonly _$tableElem: Signal<ElementRef<HTMLTableElement>> =
    viewChild.required<ElementRef<HTMLTableElement>>('table');
  private _resizeObserver!: ResizeObserver;
  private _lastContainerWidth = 0;

  constructor() {
    this._dataTableSvc.initState(
      signal({
        isCollapsed: false
      })
    );
    afterNextRender({
      read: this._setupResponsiveObserver.bind(this)
    });
  }

  ngOnDestroy(): void {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  }

  ngAfterViewInit(): void {
    this.isRendered.emit(this);
  }

  protected toggleSort(column: DataTableColumn<T>): void {
    if (column.sortable === false || this.$processedData().length <= 2) {
      return;
    }

    const CURRENT_TARGET = this.$sortColumn();
    const CURRENT_DIR = this.$sortDirection();

    if (CURRENT_TARGET === column.key) {
      // Rotate: asc -> desc -> null
      if (CURRENT_DIR === 'asc') {
        this._$sortDirection.set('desc');
      } else if (CURRENT_DIR === 'desc') {
        this._$sortDirection.set(null);
        this._$sortColumn.set(null);
      }
    } else {
      this._$sortColumn.set(column.key);
      this._$sortDirection.set('asc');
    }
  }

  protected toggleRow(rowIndex: number): void {
    const UPDATED_SET = new Set(this.$expandedRows());
    if (UPDATED_SET.has(rowIndex)) {
      UPDATED_SET.delete(rowIndex);
    } else {
      UPDATED_SET.add(rowIndex);
    }
    this._$expandedRows.set(UPDATED_SET);
  }

  protected onColumnDrop(event: CdkDragDrop<DataTableColumn<T>[]>): void {
    const UPDATED_COLUMNS = [...this.$columns()];
    moveItemInArray(UPDATED_COLUMNS, event.previousIndex, event.currentIndex);
    this.$columns.set(UPDATED_COLUMNS);
  }

  private _setupResponsiveObserver(): void {
    this._resizeObserver = new ResizeObserver(() => this._handleOverflow());
    this._resizeObserver.observe(this._hostElem.nativeElement);
    this._resizeObserver.observe(this._$tableElem().nativeElement);
  }

  private _handleOverflow(): void {
    const CONTAINER = this._hostElem.nativeElement;
    const TABLE = this._$tableElem().nativeElement;

    // If the container grows, aggressively reset hidden columns to recalculate
    if (this.$hiddenColumnKeys().size > 0 && CONTAINER.clientWidth > this._lastContainerWidth) {
      this._$hiddenColumnKeys.set(new Set());
      this._lastContainerWidth = CONTAINER.clientWidth;
      return;
    }

    this._lastContainerWidth = CONTAINER.clientWidth;

    // Hide the furthest right column if the table is breaking boundaries
    if (TABLE.scrollWidth > CONTAINER.clientWidth) {
      const VISIBLE_COLS = this.$visibleColumns();
      if (VISIBLE_COLS.length > 1) {
        const NEW_HIDDEN = new Set(this.$hiddenColumnKeys());
        NEW_HIDDEN.add(VISIBLE_COLS[VISIBLE_COLS.length - 1].key);
        this._$hiddenColumnKeys.set(NEW_HIDDEN);
      }
    }
  }

  protected isLabelComplex = this._dataTableSvc.isLabelComplex;

  set isCollapsed(isCollapsed: boolean) {
    this._dataTableSvc.isCollapsed = isCollapsed;
  }
}
